import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Zap, ArrowLeft, Globe } from 'lucide-react';

export const Terms = ({ hideLayout = false }) => {
  const [lang, setLang] = useState('en');

  useEffect(() => {
    const browserLang = navigator.language || navigator.userLanguage;
    if (browserLang.toLowerCase().startsWith('es')) {
      setLang('es');
    }
  }, []);

  const toggleLang = () => {
    setLang(prev => prev === 'en' ? 'es' : 'en');
  };

  const innerContent = (
    <>
      <div className="flex justify-between items-center mb-2">
        <h1 className={hideLayout ? "text-2xl font-extrabold" : "text-4xl font-extrabold"}>
          {lang === 'en' ? 'Terms and Conditions' : 'Términos y Condiciones'}
        </h1>
        {hideLayout && (
          <button onClick={toggleLang} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface-100 hover:bg-surface-200 dark:bg-surface-700 dark:hover:bg-surface-600 text-sm font-medium">
            <Globe size={16} />
            <span>{lang === 'es' ? 'EN' : 'ES'}</span>
          </button>
        )}
      </div>
      <p className="text-surface-500 mb-10 pb-6 border-b border-surface-100 dark:border-surface-700">
        {lang === 'en' ? 'Last updated: August 2026' : 'Última actualización: Agosto 2026'}
      </p>

      {lang === 'en' ? (
        // ENGLISH CONTENT
        <div className="space-y-8 text-surface-700 dark:text-surface-300 leading-relaxed">
          <section>
            <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">1. Acceptance of Terms</h2>
            <p>Welcome to Quinvoice. By accessing or using our website, mobile application, or any of our services ("Service"), you agree to be bound by these Terms and Conditions ("Terms"). If you disagree with any part of the terms, you may not access the Service.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">2. Description of Service & No Tax Advice</h2>
            <p className="mb-4">Quinvoice provides administrative software tools designed to help individuals and businesses manage invoices, quotes, and inventory. <strong>Quinvoice is strictly a document generation and management tool.</strong></p>
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 rounded-r-lg mb-4">
              <p className="text-amber-900 dark:text-amber-200 text-sm font-medium">
                We are NOT a certified accounting firm, tax authority, or legal advisor. Our Service does not automatically validate invoices with government entities (e.g., CFDI, DIAN, IRS). You are solely responsible for ensuring that the documents you create and send comply with the legal and tax regulations of your jurisdiction.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">3. User Accounts & Security</h2>
            <p>When you create an account, you guarantee that the information you provide is accurate, complete, and current at all times. You are responsible for safeguarding your password and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account. We will not be liable for any loss or damage arising from your failure to protect your login credentials.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">4. Subscriptions, Payments & No Refunds</h2>
            <p className="mb-3">Certain features of the Service are billed on a subscription basis ("Subscriptions"). Subscriptions are billed in advance on a recurring schedule.</p>
            <p className="mb-3 font-bold text-surface-900 dark:text-white">NO REFUNDS:</p>
            <p>All payments made to Quinvoice are strictly final and non-refundable. We do not provide refunds or credits for any partial subscription periods, unused months, or accidental purchases. If you cancel your subscription, you will retain access to the premium features until the end of your current billing cycle.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">5. Disclaimer of Warranties ("AS IS")</h2>
            <p className="mb-3 uppercase text-sm font-semibold tracking-wide text-surface-600 dark:text-surface-400">
              The Service is provided on an "AS IS" and "AS AVAILABLE" basis. Quinvoice expressly disclaims all warranties, whether express, implied, statutory, or otherwise, including but not limited to the implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
            </p>
            <p>We do not warrant that the Service will be uninterrupted, error-free, secure, or completely free of data loss. You agree that your use of the Service is at your own sole risk. You are encouraged to keep your own independent backups of your financial records.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">6. Limitation of Liability</h2>
            <p className="uppercase text-sm font-semibold tracking-wide text-surface-600 dark:text-surface-400">
              To the maximum extent permitted by applicable law, in no event shall Quinvoice, its founders, developers, employees, or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages. This includes, without limitation, loss of profits, data, business interruption, tax penalties, or any other intangible losses, resulting from your access to or use of (or inability to access or use) the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">7. Indemnification</h2>
            <p>You agree to defend, indemnify, and hold harmless Quinvoice and its licensee and licensors, and their employees, contractors, agents, officers, and directors, from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to attorney's fees) arising from your use of and access to the Service, or from any violation of these Terms by you.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">8. Account Termination</h2>
            <p>We may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of the Terms.</p>
          </section>
        </div>
      ) : (
        // SPANISH CONTENT
        <div className="space-y-8 text-surface-700 dark:text-surface-300 leading-relaxed">
          <section>
            <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">1. Aceptación de los Términos</h2>
            <p>Bienvenido a Quinvoice. Al acceder o utilizar nuestro sitio web, aplicación móvil o cualquiera de nuestros servicios ("Servicio"), usted acepta estar sujeto a estos Términos y Condiciones ("Términos"). Si no está de acuerdo con alguna parte de los términos, no podrá acceder al Servicio.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">2. Descripción del Servicio y Sin Asesoramiento Fiscal</h2>
            <p className="mb-4">Quinvoice proporciona herramientas de software administrativo diseñadas para ayudar a personas y empresas a gestionar facturas, cotizaciones e inventario. <strong>Quinvoice es estrictamente una herramienta de gestión y generación de documentos.</strong></p>
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 rounded-r-lg mb-4">
              <p className="text-amber-900 dark:text-amber-200 text-sm font-medium">
                NO somos una firma contable certificada, autoridad fiscal, ni asesores legales. Nuestro Servicio NO valida automáticamente facturas con entidades gubernamentales (ej. CFDI, DIAN, SUNAT, AFIP). Usted es el único responsable de garantizar que los documentos que cree y envíe cumplan con las regulaciones legales y fiscales de su país.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">3. Cuentas de Usuario y Seguridad</h2>
            <p>Al crear una cuenta, usted garantiza que la información que proporciona es precisa, completa y actual en todo momento. Usted es responsable de salvaguardar su contraseña y de todas las actividades que ocurran bajo su cuenta. Acepta notificarnos de inmediato sobre cualquier uso no autorizado de su cuenta. No seremos responsables de ninguna pérdida o daño derivado de su incapacidad para proteger sus credenciales de acceso.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">4. Suscripciones, Pagos y Política de No Reembolso</h2>
            <p className="mb-3">Ciertas funciones del Servicio se facturan mediante una suscripción ("Suscripciones"). Las suscripciones se cobran por adelantado en un ciclo recurrente.</p>
            <p className="mb-3 font-bold text-surface-900 dark:text-white">NO HAY REEMBOLSOS:</p>
            <p>Todos los pagos realizados a Quinvoice son estrictamente definitivos y no reembolsables. No otorgamos reembolsos ni créditos por períodos de suscripción parciales, meses no utilizados o compras accidentales. Si cancela su suscripción, conservará el acceso a las funciones premium hasta el final de su ciclo de facturación actual.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">5. Renuncia de Garantías (Cláusula "AS IS")</h2>
            <p className="mb-3 uppercase text-sm font-semibold tracking-wide text-surface-600 dark:text-surface-400">
              El Servicio se proporciona "TAL CUAL" (AS IS) y "SEGÚN DISPONIBILIDAD". Quinvoice renuncia expresamente a todas las garantías, ya sean expresas, implícitas o legales, incluyendo, entre otras, las garantías implícitas de comerciabilidad e idoneidad para un propósito particular.
            </p>
            <p>No garantizamos que el Servicio será ininterrumpido, libre de errores, seguro o completamente libre de pérdida de datos. Usted acepta que el uso del Servicio es bajo su propio riesgo. Le recomendamos encarecidamente mantener sus propias copias de seguridad independientes de sus registros financieros.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">6. Limitación de Responsabilidad</h2>
            <p className="uppercase text-sm font-semibold tracking-wide text-surface-600 dark:text-surface-400">
              En la medida máxima permitida por la ley aplicable, en ningún caso Quinvoice, sus fundadores, desarrolladores, empleados o afiliados serán responsables por ningún daño indirecto, incidental, especial, punitivo o consecuente. Esto incluye, sin limitación, pérdida de beneficios, datos, interrupción del negocio, multas fiscales o cualquier otra pérdida intangible, que resulte de su acceso o uso (o incapacidad de acceder o usar) el Servicio.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">7. Indemnización</h2>
            <p>Usted acepta defender, indemnizar y eximir de responsabilidad a Quinvoice, sus empleados, contratistas, agentes y directores, frente a todas y cada una de las reclamaciones, daños, obligaciones, pérdidas, responsabilidades, costos o deudas y gastos (incluyendo los honorarios de abogados) que surjan de su uso y acceso al Servicio, o de cualquier violación de estos Términos por su parte (ej. uso fraudulento de facturas).</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-4">8. Terminación de Cuenta</h2>
            <p>Podemos rescindir o suspender su cuenta y prohibir el acceso al Servicio de inmediato, sin previo aviso ni responsabilidad, bajo nuestro propio criterio, por cualquier motivo y sin limitación, incluyendo, entre otros, el incumplimiento de los Términos.</p>
          </section>
        </div>
      )}
    </>
  );

  if (hideLayout) {
    return <div className="text-left w-full h-full">{innerContent}</div>;
  }

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-900 text-surface-900 dark:text-gray-100 font-sans pb-20">
      <nav className="border-b border-gray-200 dark:border-surface-800 bg-white dark:bg-surface-800 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/" className="flex items-center gap-2 text-surface-600 hover:text-primary transition-colors">
              <ArrowLeft size={20} />
              <span className="font-medium">{lang === 'es' ? 'Volver al inicio' : 'Back to home'}</span>
            </Link>
            <div className="flex items-center gap-4">
              <button 
                onClick={toggleLang}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface-100 hover:bg-surface-200 dark:bg-surface-700 dark:hover:bg-surface-600 text-sm font-medium transition-colors"
              >
                <Globe size={16} />
                <span>{lang === 'es' ? 'EN' : 'ES'}</span>
              </button>
              <div className="flex items-center gap-2">
                <Zap className="h-6 w-6 text-primary" fill="currentColor" />
                <span className="font-bold text-xl tracking-tight text-surface-900 dark:text-white">Quinvoice</span>
              </div>
            </div>
          </div>
        </div>
      </nav>
      <div className="max-w-4xl mx-auto px-4 mt-12">
        <div className="bg-white dark:bg-surface-800 rounded-3xl p-8 md:p-12 shadow-sm border border-surface-200 dark:border-surface-700">
          {innerContent}
        </div>
      </div>
    </div>
  );
};
